<script setup>
// SharkFoto 音频工具(nuxt#19 P3)。复刻 design 的 audio.js + Merge/Voice Recorder 单页脚本:
//   trim / ringtone / fade / speed → Web Audio 解码 + canvas 波形 + <audio> 预览(纯浏览器)。
//   merge → 文件列表拖拽排序(浏览器内)。 record → MediaRecorder/getUserMedia 录音并下载。
// 真实的剪切/编码/合并需服务端转码,这里忠实复刻 blueprint:浏览器内做预览/选区/录音,
//   重型导出走「Process with SharkFoto (server)」优雅占位(toast),绝不引入 ffmpeg。
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { perFileMax } from "@/settings/free-tools/batch-limits.js";
import { pollJob } from "@/composables/useJobPoll.js";

const props = defineProps({
  mode: { type: String, default: "trim" }, // trim | ringtone | fade | speed | merge | record
  slug: { type: String, default: "" },
});

const isWave = computed(() => ["trim", "ringtone", "fade", "speed"].includes(props.mode));
const isSel = computed(() => props.mode === "trim" || props.mode === "ringtone"); // 有选区手柄
const ACCENT = "#22d3ee";

/* ─────────────────────────── 共享:toast ─────────────────────────── */
const toast = ref("");
let toastTimer = null;
function showToast(msg) {
  toast.value = msg;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toast.value = ""), 2600);
}
function fmt(s) {
  s = Math.max(0, s || 0);
  const m = Math.floor(s / 60), ss = Math.floor(s % 60);
  return m + ":" + String(ss).padStart(2, "0");
}

/* ═════════════════════════ 波形工具(trim/ringtone/fade/speed) ═════════════════════════ */
const stage = ref("drop"); // drop | work
const dragging = ref(false);
const fileInput = ref(null);
const waveCanvas = ref(null);
const waveWrap = ref(null);
const audioEl = ref(null);

const fileName = ref("audio.mp3");
const dur = ref(0);
const curTime = ref(0);
const playing = ref(false);
let buf = null; // AudioBuffer
let objUrl = "";
let rawFile = null; // 原始 File（重型导出时取其字节 POST 到后端，不进响应式）
const srcExt = ref("mp3"); // 上传文件的源后缀（lowercased）→ from_format / 默认 to_format

// 重型导出（后端异步转码）状态
const exporting = ref(false); // 正在 POST + 轮询服务端
const exportError = ref(""); // 友好错误/超时提示
const resultReady = ref(false); // 服务端产物已就绪，可下载
let resultBlob = null; // 服务端返回的产物 Blob（不进响应式）
let resultName = "audio.mp3"; // 产物下载文件名
let resultDownloadUrl = null; // 最近一个产物 blob: URL（换新前释放）

// 选区(0..1) + 速度 + 淡入淡出
const a = ref(0.1), b = ref(0.9);
const speed = ref(1);
const SPEEDS = [0.5, 0.75, 1, 1.5, 2];
const fadeIn = ref(2), fadeOut = ref(2);
const outFmt = ref("mp3");
const FMTS = ["mp3", "wav", "m4a"];

const selDurSec = computed(() => Math.max(0, (b.value - a.value) * dur.value));
const startLabel = computed(() => fmt(a.value * dur.value));
const endLabel = computed(() => fmt(b.value * dur.value));

function revoke() { if (objUrl) { URL.revokeObjectURL(objUrl); objUrl = ""; } }

async function loadFile(file) {
  if (!file || !file.type.startsWith("audio/")) { alert("Please choose an audio file."); return; }
  const _max = perFileMax("audio");
  if (file.size > _max) { alert(`File is too large (${(file.size / 1048576).toFixed(1)} MB). Max is ${Math.round(_max / 1048576)} MB for this tool.`); return; }
  fileName.value = file.name;
  rawFile = file; // 留给重型导出取字节
  srcExt.value = (file.name.match(/\.([a-z0-9]+)$/i) || [, "mp3"])[1].toLowerCase();
  clearExport();
  revoke();
  objUrl = URL.createObjectURL(file);
  stage.value = "work";
  await nextTick();
  const el = audioEl.value;
  if (el) {
    el.src = objUrl;
    el.playbackRate = speed.value;
    el.addEventListener("loadedmetadata", () => {
      if (!dur.value || !isFinite(dur.value)) {
        dur.value = el.duration || 0;
        if (props.mode === "ringtone") { a.value = 0; b.value = Math.min(1, 30 / (dur.value || 30)); }
        drawWave();
      }
    }, { once: true });
  }
  try {
    const ab = await file.arrayBuffer();
    const AC = window.AudioContext || window.webkitAudioContext;
    window.__sharedAC = window.__sharedAC || new AC();
    buf = await window.__sharedAC.decodeAudioData(ab);
    dur.value = buf.duration;
    if (props.mode === "ringtone") { a.value = 0; b.value = Math.min(1, 30 / dur.value); }
    else { a.value = 0.1; b.value = 0.9; }
    drawWave();
  } catch (e) { /* 解码失败:仅依赖 <audio> 元数据 */ }
}

function drawWave() {
  const cv = waveCanvas.value;
  if (!cv || !buf) return;
  const ctx = cv.getContext("2d");
  const W = cv.width = cv.clientWidth * 2, H = cv.height = cv.clientHeight * 2;
  ctx.clearRect(0, 0, W, H);
  const data = buf.getChannelData(0), step = Math.floor(data.length / W) || 1, mid = H / 2;
  const av = a.value, bv = b.value, d = dur.value || 1;

  if (isSel.value) {
    ctx.fillStyle = "rgba(0,0,0,.5)"; ctx.fillRect(0, 0, av * W, H); ctx.fillRect(bv * W, 0, (1 - bv) * W, H);
    ctx.fillStyle = "rgba(34,211,238,.16)"; ctx.fillRect(av * W, 0, (bv - av) * W, H);
    ctx.fillStyle = ACCENT; ctx.fillRect(av * W - 3, 0, 6, H); ctx.fillRect(bv * W - 3, 0, 6, H);
  }
  ctx.strokeStyle = isSel.value ? "rgba(255,255,255,.5)" : ACCENT; ctx.lineWidth = 2; ctx.beginPath();
  for (let x = 0; x < W; x++) {
    let mn = 1, mx = -1;
    for (let i = 0; i < step; i++) { const v = data[x * step + i] || 0; if (v < mn) mn = v; if (v > mx) mx = v; }
    ctx.moveTo(x, mid + mn * mid * 0.92); ctx.lineTo(x, mid + mx * mid * 0.92);
  }
  ctx.stroke();
  // 选区内的波形用高亮 accent 重描
  if (isSel.value) {
    ctx.save(); ctx.beginPath(); ctx.rect(av * W, 0, (bv - av) * W, H); ctx.clip();
    ctx.strokeStyle = ACCENT; ctx.lineWidth = 2; ctx.beginPath();
    for (let x = Math.floor(av * W); x < bv * W; x++) {
      let mn = 1, mx = -1;
      for (let i = 0; i < step; i++) { const v = data[x * step + i] || 0; if (v < mn) mn = v; if (v > mx) mx = v; }
      ctx.moveTo(x, mid + mn * mid * 0.92); ctx.lineTo(x, mid + mx * mid * 0.92);
    }
    ctx.stroke(); ctx.restore();
  }
  // 淡入淡出三角覆盖
  if (props.mode === "fade") {
    ctx.fillStyle = "rgba(34,211,238,.14)";
    if (fadeIn.value > 0) { ctx.beginPath(); ctx.moveTo(0, H); ctx.lineTo(0, mid); ctx.lineTo((fadeIn.value / d) * W, H); ctx.closePath(); ctx.fill(); }
    if (fadeOut.value > 0) { ctx.beginPath(); ctx.moveTo(W, H); ctx.lineTo(W, mid); ctx.lineTo(W - (fadeOut.value / d) * W, H); ctx.closePath(); ctx.fill(); }
  }
}

/* 选区手柄拖拽(trim/ringtone) */
let drag = null;
function onWaveDown(e) {
  if (!isSel.value) return;
  const r = waveWrap.value.getBoundingClientRect();
  const x = (e.clientX - r.left) / r.width;
  drag = Math.abs(x - a.value) < Math.abs(x - b.value) ? "a" : "b";
  moveHandle(e);
}
function moveHandle(e) {
  const r = waveWrap.value.getBoundingClientRect();
  let x = (e.clientX - r.left) / r.width;
  x = Math.max(0, Math.min(1, x));
  if (drag === "a") a.value = Math.min(x, b.value - 0.02);
  else b.value = Math.max(x, a.value + 0.02);
  if (props.mode === "ringtone" && (b.value - a.value) * dur.value > 30) {
    if (drag === "a") b.value = a.value + 30 / dur.value; else a.value = b.value - 30 / dur.value;
  }
  drawWave();
}
function onWinMove(e) { if (drag) moveHandle(e); }
function onWinUp() { drag = null; }

/* 播放/暂停 + 速度 */
function togglePlay() {
  const el = audioEl.value; if (!el) return;
  if (el.paused) { if (isSel.value) el.currentTime = a.value * dur.value; el.play(); }
  else el.pause();
}
function onTimeUpdate() {
  const el = audioEl.value; if (!el) return;
  curTime.value = el.currentTime;
  if (isSel.value && el.currentTime >= b.value * dur.value) el.pause();
}
watch(speed, (v) => { const el = audioEl.value; if (el) el.playbackRate = +v; });
watch([fadeIn, fadeOut], drawWave);

// 改任一导出参数（选区 / 速度 / 淡入淡出 / 输出格式）→ 作废旧产物，主按钮回到 Export。
watch([a, b, speed, fadeIn, fadeOut, outFmt], () => { if (resultReady.value) clearExport(); });

/* ───────── 重型导出：POST 原始字节 → 后端异步转码（202 + jobId）→ 轮询 /job → 取 /result ─────────
   异步流程与 ToolStagePlaceholder.vue 的 onConvert(res.status===202 分支) 完全一致，逐字复刻。 */
function clearExport() {
  exporting.value = false;
  exportError.value = "";
  resultReady.value = false;
  resultBlob = null;
  if (resultDownloadUrl) { URL.revokeObjectURL(resultDownloadUrl); resultDownloadUrl = null; }
}

// 按当前 mode 构造 op + 参数。返回 { op, params, to } 或 null（不应走后端的 mode）。
function buildOp() {
  const from = srcExt.value;
  const start = a.value * dur.value;
  if (props.mode === "trim") {
    const duration = Math.max(0.05, (b.value - a.value) * dur.value);
    // 输出格式用 UI 上已有的 Output format 选择（mp3/wav/m4a），默认回退到源后缀。
    return { op: "trim", to: outFmt.value || from, params: "&start=" + start.toFixed(3) + "&duration=" + duration.toFixed(3) };
  }
  if (props.mode === "ringtone") {
    // 铃声固定输出 mp3，时长夹到 ≤30s。
    const duration = Math.min(30, Math.max(0.05, (b.value - a.value) * dur.value));
    return { op: "trim", to: "mp3", params: "&start=" + start.toFixed(3) + "&duration=" + duration.toFixed(3) };
  }
  if (props.mode === "fade") {
    return { op: "fade", to: from, params: "&fade_in=" + Number(fadeIn.value) + "&fade_out=" + Number(fadeOut.value) };
  }
  if (props.mode === "speed") {
    return { op: "speed", to: from, params: "&speed=" + Number(speed.value) };
  }
  return null;
}

async function exportWave() {
  if (!import.meta.client || exporting.value || !rawFile) return;
  const job = buildOp();
  if (!job) return;
  exportError.value = "";
  exporting.value = true;
  resultReady.value = false;
  try {
    const from = srcExt.value;
    const qs =
      "to_format=" + encodeURIComponent(job.to) +
      "&from_format=" + encodeURIComponent(from) +
      "&op=" + encodeURIComponent(job.op) +
      job.params;
    const res = await fetch("/api/free-tools/convert?" + qs, {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: await rawFile.arrayBuffer(),
    });
    // 视频/音频走异步：202 + jobId → 每 1500ms 轮询 /job（最多 ~120 次）到 done，再取 /result 字节。
    if (res.status === 202) {
      const { jobId } = await res.json();
      // 退避 + 长上限轮询,覆盖后端重转换真实耗时(见 useJobPoll)。
      const jb = await pollJob(jobId);
      if (!jb || jb.status !== "done") {
        exportError.value = (jb && jb.detail) || "Processing timed out. Please try again or use a shorter selection.";
        return;
      }
      const rr = await fetch("/api/free-tools/result/" + encodeURIComponent(jobId), { cache: "no-store" });
      const blob = await rr.blob();
      const ext = (jb.ext || job.to) === "jpeg" ? "jpg" : (jb.ext || job.to);
      finishExport(blob, ext);
      return;
    }
    // 后端若同步返回（非 202）：直接取字节（防御性兜底，与占位行为兼容）。
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = j && j.detail ? j.detail : ""; } catch (_) { /* not JSON */ }
      exportError.value = detail || "Processing failed (" + res.status + "). Please try again.";
      return;
    }
    const blob = await res.blob();
    finishExport(blob, job.to);
  } catch (err) {
    exportError.value = "Could not reach the processing server. Please try again.";
  } finally {
    exporting.value = false;
  }
}

function finishExport(blob, ext) {
  if (resultDownloadUrl) { URL.revokeObjectURL(resultDownloadUrl); resultDownloadUrl = null; }
  resultBlob = blob;
  resultDownloadUrl = URL.createObjectURL(blob);
  // merge 模式没有单一上传文件名（fileName 仍是默认值），基名固定 "merged"；其它模式取上传文件名去后缀。
  const base = props.mode === "merge"
    ? "merged"
    : (fileName.value.replace(/\.[^.]+$/, "") || "audio");
  const suffix =
    props.mode === "merge" ? "" // merge 不加后缀 → merged.mp3
    : props.mode === "ringtone" ? "-ringtone"
    : props.mode === "fade" ? "-faded"
    : props.mode === "speed" ? "-" + speed.value + "x"
    : "-trimmed";
  resultName = base + suffix + "." + ext;
  resultReady.value = true;
  showToast("Done — your " + ext.toUpperCase() + " file is ready to download.");
}

// 手动下载：走 app 的 /download/ 页（同源新标签页读 blob: URL），与 ToolStagePlaceholder 一致。
function downloadResult() {
  if (!import.meta.client || !resultBlob || !resultDownloadUrl) return;
  window.open(
    "/download/?img_title=" + encodeURIComponent(resultName) +
      "&img_path=" + encodeURIComponent(resultDownloadUrl) +
      "&prev_route1=/free-tools/audio&prev_route1_title=AudioTools"
  );
}

// 波形导出区主按钮：导出中→Processing；产物就绪→Download；否则→Export。
const wavePrimaryLabel = computed(() => {
  if (exporting.value) return "Processing on our servers…";
  if (resultReady.value) return "Download";
  return exportLabel.value;
});
function onWavePrimary() {
  if (exporting.value) return;
  if (resultReady.value) downloadResult();
  else exportWave();
}

function resetWave() {
  const el = audioEl.value; if (el) { el.pause(); el.removeAttribute("src"); el.load(); }
  stage.value = "drop"; buf = null; dur.value = 0; curTime.value = 0; playing.value = false;
  a.value = 0.1; b.value = 0.9; revoke();
  rawFile = null;
  clearExport();
  if (fileInput.value) fileInput.value.value = "";
}

/* ═════════════════════════ 合并(merge) ═════════════════════════ */
const tracks = ref([]); // {name, file}（file 为原始 File，重型合并时 multipart 上传）
const mergeInput = ref(null);
function addFiles(files) {
  const _max = perFileMax("audio");
  const arr = [...files].filter((f) => f.type.startsWith("audio/"));
  const _big = arr.find((f) => f.size > _max);
  if (_big) { showToast(`File is too large (${(_big.size / 1048576).toFixed(1)} MB). Max is ${Math.round(_max / 1048576)} MB.`); return; }
  arr.forEach((f) => tracks.value.push({ name: f.name, file: f }));
  if (tracks.value.length) stage.value = "work";
}
function delTrack(i) { tracks.value.splice(i, 1); if (!tracks.value.length) resetMerge(); }
let dragFrom = null;
function onRowDragStart(i, e) { dragFrom = i; e.dataTransfer.effectAllowed = "move"; }
function onRowDragOver(i, e) {
  e.preventDefault();
  if (dragFrom === null || i === dragFrom) return;
  const m = tracks.value.splice(dragFrom, 1)[0];
  tracks.value.splice(i, 0, m);
  dragFrom = i;
}
function onRowDragEnd() { dragFrom = null; }
// 重型合并：multipart 上传 N(>=2) 个 File → /merge → 202 + jobId → 轮询 /job → 取 /result。
//   202→轮询→/result 逻辑与 exportWave 逐字一致，仅 body 换 FormData、URL 换 /merge。
async function runMergeExport() {
  if (!import.meta.client || exporting.value || tracks.value.length < 2) return;
  clearExport(); // 清空上一次的 error/产物（也会把 exporting 置回 false）
  exporting.value = true;
  try {
    const fd = new FormData();
    tracks.value.forEach((t) => fd.append("files", t.file));
    const res = await fetch("/api/free-tools/merge?to_format=mp3", { method: "POST", body: fd }); // 不要设 headers
    // 视频/音频走异步：202 + jobId → 每 1500ms 轮询 /job（最多 ~120 次）到 done，再取 /result 字节。
    if (res.status === 202) {
      const { jobId } = await res.json();
      // 退避 + 长上限轮询,覆盖后端重转换真实耗时(见 useJobPoll)。
      const jb = await pollJob(jobId);
      if (!jb || jb.status !== "done") {
        exportError.value = (jb && jb.detail) || "Processing timed out. Please try again or use a shorter selection.";
        return;
      }
      const rr = await fetch("/api/free-tools/result/" + encodeURIComponent(jobId), { cache: "no-store" });
      const blob = await rr.blob();
      const ext = (jb.ext || "mp3") === "jpeg" ? "jpg" : (jb.ext || "mp3");
      finishExport(blob, ext);
      return;
    }
    // 后端若同步返回（非 202）：直接取字节（防御性兜底）。
    if (!res.ok) {
      let detail = "";
      try { const j = await res.json(); detail = j && j.detail ? j.detail : ""; } catch (_) { /* not JSON */ }
      exportError.value = detail || "Processing failed (" + res.status + "). Please try again.";
      return;
    }
    const blob = await res.blob();
    finishExport(blob, "mp3");
  } catch (err) {
    exportError.value = "Could not reach the processing server. Please try again.";
  } finally {
    exporting.value = false;
  }
}
function exportMerge() { runMergeExport(); }

// merge 导出区主按钮：导出中→Processing；产物就绪→Download；否则→Merge & export。
const mergePrimaryLabel = computed(() => {
  if (exporting.value) return "Processing on our servers…";
  if (resultReady.value) return "Download";
  return exportLabel.value;
});
function onMergePrimary() {
  if (exporting.value) return;
  if (resultReady.value) downloadResult();
  else exportMerge();
}
function resetMerge() { stage.value = "drop"; tracks.value = []; clearExport(); if (mergeInput.value) mergeInput.value.value = ""; }

// 改动合并列表（增删 / 重排）→ 作废旧产物，主按钮回到 Merge & export。仅 merge 模式。
watch(() => tracks.value.map((t) => t.name).join("|"), () => {
  if (props.mode === "merge" && resultReady.value) clearExport();
});

/* ═════════════════════════ 录音(record) ═════════════════════════ */
const recBtn = ref(null);
const recAudio = ref(null);
const recTimer = ref("0:00");
const recStatus = ref("Tap the button to start recording");
const recording = ref(false);
const hasRec = ref(false);
const recExt = ref("");
const recDlHref = ref("");
const recDlName = ref("recording.webm");
const VIZ_N = 32;
const bars = ref(Array.from({ length: VIZ_N }, () => 8));

let mr = null, chunks = [], stream = null, recStart = 0, recTick = null;
let analyser = null, recAC = null, raf = null, recUrl = "";

function drawViz() {
  if (!analyser) return;
  const d = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteFrequencyData(d);
  const next = bars.value.slice();
  for (let i = 0; i < VIZ_N; i++) { const v = d[Math.floor((i / VIZ_N) * d.length)] / 255; next[i] = 8 + v * 68; }
  bars.value = next;
  raf = requestAnimationFrame(drawViz);
}

async function startRec() {
  try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
  catch (e) { recStatus.value = "Microphone access denied. Please allow it and try again."; return; }
  recAC = new (window.AudioContext || window.webkitAudioContext)();
  const src = recAC.createMediaStreamSource(stream);
  analyser = recAC.createAnalyser(); analyser.fftSize = 64; src.connect(analyser); drawViz();

  let mime = "audio/webm";
  if (window.MediaRecorder && MediaRecorder.isTypeSupported) {
    if (MediaRecorder.isTypeSupported("audio/mp4")) mime = "audio/mp4";
    else if (MediaRecorder.isTypeSupported("audio/ogg")) mime = "audio/ogg";
    else if (MediaRecorder.isTypeSupported("audio/webm")) mime = "audio/webm";
  }
  mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  chunks = [];
  mr.ondataavailable = (e) => chunks.push(e.data);
  mr.onstop = async () => {
    const mt = mr.mimeType || "audio/webm";
    const blob = new Blob(chunks, { type: mt });
    // 从字节嗅探真实容器(部分浏览器会误报 mimeType)
    let ext = "webm";
    try {
      const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
      if (head[0] === 0x1A && head[1] === 0x45 && head[2] === 0xDF && head[3] === 0xA3) ext = "webm";
      else if (head[4] === 0x66 && head[5] === 0x74 && head[6] === 0x79 && head[7] === 0x70) ext = "m4a"; // 'ftyp'
      else if (head[0] === 0x4F && head[1] === 0x67 && head[2] === 0x67 && head[3] === 0x53) ext = "ogg"; // 'OggS'
      else ext = mt.includes("mp4") ? "m4a" : mt.includes("ogg") ? "ogg" : "webm";
    } catch (e) { ext = mt.includes("ogg") ? "ogg" : "webm"; }
    if (recUrl) URL.revokeObjectURL(recUrl);
    recUrl = URL.createObjectURL(blob);
    if (recAudio.value) recAudio.value.src = recUrl;
    recExt.value = ext.toUpperCase();
    recDlHref.value = recUrl;
    recDlName.value = "recording." + ext;
    hasRec.value = true;
    if (stream) stream.getTracks().forEach((t) => t.stop());
    cancelAnimationFrame(raf);
    bars.value = Array.from({ length: VIZ_N }, () => 8);
  };
  mr.start();
  recording.value = true; hasRec.value = false;
  recStart = Date.now();
  recStatus.value = "Recording… tap to stop";
  recTick = setInterval(() => (recTimer.value = fmt((Date.now() - recStart) / 1000)), 250);
}
function stopRec() {
  recording.value = false;
  clearInterval(recTick);
  recStatus.value = "Recorded — play it back or download";
  if (mr && mr.state !== "inactive") mr.stop();
}
function toggleRec() { recording.value ? stopRec() : startRec(); }
function discardRec() {
  hasRec.value = false; recTimer.value = "0:00";
  recStatus.value = "Tap the button to start recording";
  if (recAudio.value) recAudio.value.removeAttribute("src");
  if (recUrl) { URL.revokeObjectURL(recUrl); recUrl = ""; }
}

/* ─────────────────────────── 拖放 + 生命周期 ─────────────────────────── */
function pick() { fileInput.value && fileInput.value.click(); }
function onInput(e) { const f = e.target.files && e.target.files[0]; if (f) loadFile(f); }
function onDrop(e) { e.preventDefault(); dragging.value = false; const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) loadFile(f); }
function pickMerge() { mergeInput.value && mergeInput.value.click(); }
function onMergeInput(e) { if (e.target.files && e.target.files.length) addFiles(e.target.files); e.target.value = ""; }
function onMergeDrop(e) { e.preventDefault(); dragging.value = false; if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files); }

function onResize() { if (isWave.value) drawWave(); }
onMounted(() => {
  window.addEventListener("pointermove", onWinMove);
  window.addEventListener("pointerup", onWinUp);
  window.addEventListener("resize", onResize);
});
onBeforeUnmount(() => {
  window.removeEventListener("pointermove", onWinMove);
  window.removeEventListener("pointerup", onWinUp);
  window.removeEventListener("resize", onResize);
  clearTimeout(toastTimer); clearInterval(recTick); clearInterval(recTick);
  cancelAnimationFrame(raf);
  if (stream) stream.getTracks().forEach((t) => t.stop());
  revoke();
  if (recUrl) URL.revokeObjectURL(recUrl);
  if (resultDownloadUrl) { URL.revokeObjectURL(resultDownloadUrl); resultDownloadUrl = null; }
});

/* 文案 */
const dzTitle = computed(() => (props.mode === "merge" ? "Drop 2 or more audio files" : "Drop your audio here"));
const exportLabel = computed(() => ({
  trim: "Export trimmed audio", ringtone: "Export ringtone", fade: "Export faded audio",
  speed: "Export audio", merge: "Merge & export",
}[props.mode] || "Export audio"));
const anotherLabel = computed(() => ({
  trim: "Trim another", ringtone: "Make another", fade: "Fade another", speed: "Change another",
}[props.mode] || "Use another file"));
</script>

<template>
  <div class="at" :style="{ '--at-acc': ACCENT }">
    <!-- ════════════ 录音(record):无上传,独立舞台 ════════════ -->
    <div v-if="mode === 'record'" class="rec-stage">
      <div class="rec-timer">{{ recTimer }}</div>
      <div class="rec-status">{{ recStatus }}</div>
      <div class="rec-viz">
        <span v-for="(h, i) in bars" :key="i" :style="{ height: h + 'px' }"></span>
      </div>
      <button ref="recBtn" class="rec-btn" :class="{ recording }" aria-label="Record" type="button" @click="toggleRec">
        <svg v-if="!recording" viewBox="0 0 24 24" fill="currentColor"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0014 0M12 18v3" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" /></svg>
        <svg v-else viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
      </button>
      <div class="rec-actions" :class="{ show: hasRec }">
        <button class="btn btn-outline" type="button" @click="discardRec">Discard</button>
        <a class="btn btn-amber btn-lg" :href="recDlHref" :download="recDlName">Download .{{ recExt }}</a>
      </div>
      <div class="rec-player" :class="{ show: hasRec }"><audio ref="recAudio" controls></audio></div>
      <div v-if="hasRec" class="rec-hint">Recorded as .{{ recExt }} (your browser’s native format). Need MP3? Convert it with our Audio Converter.</div>
      <div class="rec-hint">Your microphone audio is recorded right in your browser and is never stored on a server.</div>
    </div>

    <!-- ════════════ 合并(merge) ════════════ -->
    <template v-else-if="mode === 'merge'">
      <input ref="mergeInput" type="file" accept="audio/*" multiple hidden @change="onMergeInput" />
      <div v-show="stage === 'drop'" class="dz" :class="{ drag: dragging }"
        @click="pickMerge" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false" @drop="onMergeDrop">
        <div class="dz-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l10-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></div>
        <h3>{{ dzTitle }}</h3>
        <div class="hint">or click to browse — MP3, WAV, M4A, OGG</div>
        <div class="formats"><span>MP3</span><span>WAV</span><span>M4A</span><span>OGG</span></div>
        <div class="dz-or">Processed securely — never stored</div>
        <button class="btn btn-amber" type="button" @click.stop="pickMerge">Choose Audio</button>
      </div>

      <div v-if="stage === 'work'" class="ws ws-merge">
        <div class="m-head">
          <div class="thumb"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 18V5l10-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></div>
          <div class="meta"><div class="fn">Merge order</div><div class="fsz">{{ tracks.length }} {{ tracks.length === 1 ? 'track' : 'tracks' }}</div></div>
          <button class="rm" type="button" title="Clear" @click="resetMerge"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></button>
        </div>
        <div class="alist">
          <div v-for="(t, i) in tracks" :key="i" class="arow" draggable="true"
            :class="{ dragging: dragFrom === i }"
            @dragstart="onRowDragStart(i, $event)" @dragover="onRowDragOver(i, $event)" @dragend="onRowDragEnd">
            <span class="drag" title="Drag to reorder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" /></svg></span>
            <span class="ord">{{ i + 1 }}</span>
            <span class="aico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 18V5l10-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></span>
            <span class="an">{{ t.name }}</span>
            <span class="adel" @click="delTrack(i)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></span>
          </div>
        </div>
        <p class="note">Files join top-to-bottom in this order. Add more or remove any, then export one combined track.</p>

        <!-- 服务端产物就绪提示 -->
        <div v-if="resultReady" class="result-pill show">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg>
          <span>Ready — click Download to save your merged track.</span>
        </div>
        <!-- 处理中提示 -->
        <div v-else-if="exporting" class="proc-note">
          <span class="spinner" aria-hidden="true"></span>
          <span>Processing on our servers… this can take a moment.</span>
        </div>
        <!-- 友好错误 -->
        <div v-if="exportError" class="err-note">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex:none"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" stroke-linecap="round" /></svg>
          {{ exportError }}
        </div>

        <div class="ws-actions">
          <button class="btn btn-amber btn-lg" type="button" :disabled="exporting"
            :style="exporting ? 'pointer-events:none;opacity:.7' : ''" @click="onMergePrimary">
            <svg v-if="resultReady" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-3px;margin-right:7px"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke-linecap="round" stroke-linejoin="round" /></svg>{{ mergePrimaryLabel }}
          </button>
          <button class="btn btn-outline" type="button" @click="pickMerge">Add more audio</button>
          <button class="btn btn-ghost" type="button" @click="resetMerge">Start over</button>
        </div>
      </div>
    </template>

    <!-- ════════════ 波形(trim/ringtone/fade/speed) ════════════ -->
    <template v-else>
      <input ref="fileInput" type="file" accept="audio/*" hidden @change="onInput" />
      <div v-show="stage === 'drop'" class="dz" :class="{ drag: dragging }"
        @click="pick" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false" @drop="onDrop">
        <div class="dz-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l10-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></div>
        <h3>{{ dzTitle }}</h3>
        <div class="hint">or click to browse — MP3, WAV, M4A, OGG, AAC</div>
        <div class="formats"><span>MP3</span><span>WAV</span><span>M4A</span><span>OGG</span><span>AAC</span></div>
        <div class="dz-or">Processed securely — never stored</div>
        <button class="btn btn-amber" type="button" @click.stop="pick">Choose Audio</button>
      </div>

      <div v-if="stage === 'work'" class="ws">
        <div class="ws-grid">
          <div class="ws-left">
            <div class="ws-file">
              <div class="thumb"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 18V5l10-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></div>
              <div class="meta"><div class="fn">{{ fileName }}</div><div class="fsz">{{ fmt(curTime) }} / {{ fmt(dur) }}</div></div>
              <button class="rm" type="button" title="Remove" @click="resetWave"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></button>
            </div>
            <div ref="waveWrap" class="wave-wrap" :class="{ trim: isSel }" @pointerdown="onWaveDown">
              <canvas ref="waveCanvas"></canvas>
            </div>
            <div class="audio-transport">
              <button class="tp-btn" type="button" @click="togglePlay">
                <svg v-if="!playing" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
              </button>
              <div v-if="isSel" class="tp-time">Selection: <b>{{ startLabel }}</b> – <b>{{ endLabel }}</b></div>
              <div v-else-if="mode === 'speed'" class="tp-time">Speed preview</div>
              <div v-else class="tp-time">Fade preview</div>
              <div class="tp-spacer"></div>
              <div v-if="isSel" class="tp-time">{{ selDurSec.toFixed(1) }}s</div>
            </div>
            <audio ref="audioEl" style="display:none"
              @play="playing = true" @pause="playing = false" @timeupdate="onTimeUpdate"></audio>
          </div>

          <div class="ws-panel">
            <!-- trim / ringtone -->
            <template v-if="isSel">
              <div class="ctl"><label>Start</label><div class="field"><input type="text" :value="startLabel" readonly /><span class="unit">mm:ss</span></div></div>
              <div class="ctl"><label>End</label><div class="field"><input type="text" :value="endLabel" readonly /><span class="unit">mm:ss</span></div></div>
              <div class="ctl"><label>Output format</label>
                <div class="seg audio-seg">
                  <button v-for="f in FMTS" :key="f" type="button" :class="{ active: outFmt === f }" @click="outFmt = f">{{ f.toUpperCase() }}</button>
                </div>
              </div>
              <p class="note">Drag the cyan handles on the waveform to set the start and end. Preview plays just your selection.</p>
            </template>
            <!-- speed -->
            <template v-else-if="mode === 'speed'">
              <div class="ctl"><label>Speed <span class="hl">{{ speed }}×</span></label>
                <div class="rangewrap"><input type="range" min="0.5" max="2" step="0.05" v-model.number="speed" style="flex:1" /></div>
              </div>
              <div class="seg audio-seg">
                <button v-for="s in SPEEDS" :key="s" type="button" :class="{ active: +speed === s }" @click="speed = s">{{ s }}×</button>
              </div>
              <p class="note">Drag the slider and press play to hear the new speed live. Pitch handling is set on export.</p>
            </template>
            <!-- fade -->
            <template v-else>
              <div class="ctl"><label>Fade in <span class="hl">{{ fadeIn }}s</span></label>
                <div class="rangewrap"><input type="range" min="0" max="10" step="0.5" v-model.number="fadeIn" style="flex:1" /></div>
              </div>
              <div class="ctl"><label>Fade out <span class="hl">{{ fadeOut }}s</span></label>
                <div class="rangewrap"><input type="range" min="0" max="10" step="0.5" v-model.number="fadeOut" style="flex:1" /></div>
              </div>
              <p class="note">Set how many seconds to fade in at the start and fade out at the end. The export applies a smooth volume curve.</p>
            </template>

            <!-- 服务端产物就绪提示 -->
            <div v-if="resultReady" class="result-pill show">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" /></svg>
              <span>Ready — click Download to save your file.</span>
            </div>
            <!-- 处理中提示 -->
            <div v-else-if="exporting" class="proc-note">
              <span class="spinner" aria-hidden="true"></span>
              <span>Processing on our servers… this can take a moment.</span>
            </div>
            <!-- 友好错误 -->
            <div v-if="exportError" class="err-note">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex:none"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.01" stroke-linecap="round" /></svg>
              {{ exportError }}
            </div>

            <div class="ws-actions">
              <button class="btn btn-amber btn-lg" type="button" :disabled="exporting"
                :style="exporting ? 'pointer-events:none;opacity:.7' : ''" @click="onWavePrimary">
                <svg v-if="resultReady" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-3px;margin-right:7px"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke-linecap="round" stroke-linejoin="round" /></svg>{{ wavePrimaryLabel }}
              </button>
              <button class="btn btn-outline" type="button" @click="resetWave">{{ anotherLabel }}</button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- toast -->
    <div class="at-toast" :class="{ show: toast }">{{ toast }}</div>
  </div>
</template>

<style scoped>
.at { width: 100%; max-width: 860px; margin: 0 auto; --at-acc: #22d3ee; }
.hl { color: #8fe9f5; }

/* dropzone */
.dz { position: relative; border: 2px dashed var(--line-strong); border-radius: 22px; background: linear-gradient(180deg, var(--surface), var(--bg-2)); padding: 56px 30px; text-align: center; cursor: pointer; transition: 0.2s; }
.dz.drag, .dz:hover { border-color: var(--at-acc); background: linear-gradient(180deg, color-mix(in oklab, var(--at-acc) 8%, var(--surface)), var(--bg-2)); }
.dz-ico { width: 60px; height: 60px; margin: 0 auto 14px; border-radius: 16px; display: grid; place-items: center; background: color-mix(in oklab, var(--at-acc) 16%, transparent); color: var(--at-acc); }
.dz-ico svg { width: 30px; height: 30px; }
.dz h3 { font-size: 23px; margin-bottom: 9px; font-weight: 700; }
.dz .hint { color: var(--tx-dim); font-size: 15px; }
.dz .formats { margin-top: 20px; display: flex; flex-wrap: wrap; gap: 7px; justify-content: center; }
.dz .formats span { font-size: 12px; font-weight: 700; letter-spacing: 0.03em; color: var(--tx-faint); border: 1px solid var(--line); border-radius: 7px; padding: 4px 9px; }
.dz-or { margin-top: 22px; color: var(--tx-faint); font-size: 13px; font-weight: 600; }
.dz .btn-amber { margin-top: 14px; background: linear-gradient(135deg, #0891b2, var(--at-acc)); color: #04232b; }

/* workspace */
.ws { border: 1px solid var(--line); border-radius: 22px; background: var(--surface); overflow: hidden; }
.ws-grid { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
.ws-left { padding: 26px; min-width: 0; }
.ws-panel { border-left: 1px solid var(--line); padding: 26px; display: flex; flex-direction: column; gap: 20px; }
.ws-file { display: flex; align-items: center; gap: 13px; padding-bottom: 18px; margin-bottom: 16px; border-bottom: 1px solid var(--line); }
.ws-file .thumb, .m-head .thumb { width: 46px; height: 46px; border-radius: 11px; flex: none; background: var(--surface-2); display: grid; place-items: center; color: var(--at-acc); }
.ws-file .meta { min-width: 0; flex: 1; }
.ws-file .fn, .m-head .fn { font-weight: 700; font-size: 14.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ws-file .fsz, .m-head .fsz { font-size: 12.5px; color: var(--tx-faint); font-weight: 600; margin-top: 2px; }
.ws-file .rm, .m-head .rm { color: var(--tx-faint); cursor: pointer; flex: none; padding: 6px; border-radius: 8px; background: none; border: none; transition: 0.15s; }
.ws-file .rm:hover, .m-head .rm:hover { color: #ff6f9c; background: rgba(255, 255, 255, 0.05); }

/* waveform */
.wave-wrap { position: relative; background: var(--surface-2); border: 1px solid var(--line); border-radius: 14px; padding: 14px; height: 160px; touch-action: none; }
.wave-wrap.trim { cursor: ew-resize; }
.wave-wrap canvas { width: 100%; height: 100%; display: block; }
.audio-transport { display: flex; align-items: center; gap: 14px; margin-top: 14px; }
.tp-btn { width: 44px; height: 44px; border-radius: 50%; border: 1px solid var(--line); background: var(--surface-2); color: var(--tx); display: grid; place-items: center; cursor: pointer; flex: none; transition: 0.15s; }
.tp-btn:hover { border-color: var(--at-acc); color: #8fe9f5; }
.tp-btn svg { width: 18px; height: 18px; }
.tp-time { font-family: var(--font-h); font-weight: 600; font-size: 13.5px; color: var(--tx-dim); font-variant-numeric: tabular-nums; }
.tp-time b { color: #8fe9f5; }
.tp-spacer { flex: 1; }

/* controls */
.ctl { display: flex; flex-direction: column; gap: 9px; }
.ctl label { font-size: 13px; font-weight: 700; color: var(--tx-dim); letter-spacing: 0.02em; }
.seg { display: flex; gap: 6px; flex-wrap: wrap; }
.seg button { flex: 1; min-width: 56px; padding: 10px; border-radius: 10px; border: 1px solid var(--line); background: var(--surface-2); color: var(--tx-dim); font-family: var(--font-b); font-weight: 700; font-size: 13.5px; cursor: pointer; transition: 0.15s; }
.seg button:hover { color: var(--tx); border-color: var(--line-strong); }
.seg button.active { background: color-mix(in oklab, var(--at-acc) 16%, transparent); border-color: var(--at-acc); color: #8fe9f5; }
.field { display: flex; gap: 10px; }
.field input[type="text"] { flex: 1; min-width: 0; background: var(--surface-2); border: 1px solid var(--line); border-radius: 10px; padding: 11px 13px; color: var(--tx); font-family: var(--font-b); font-size: 15px; font-weight: 600; outline: none; }
.field input:focus { border-color: var(--at-acc); }
.field .unit { display: grid; place-items: center; padding: 0 13px; border: 1px solid var(--line); border-radius: 10px; color: var(--tx-faint); font-size: 13px; font-weight: 700; background: var(--surface-2); }
.rangewrap { display: flex; align-items: center; gap: 14px; }
.rangewrap input[type="range"] { accent-color: var(--at-acc); }
.note { font-size: 12.5px; color: var(--tx-faint); line-height: 1.5; }

/* 重型导出：处理中 / 就绪 / 错误提示 */
.proc-note { display: flex; align-items: center; gap: 10px; font-size: 12.5px; color: var(--tx-dim); line-height: 1.5; }
.proc-note .spinner { width: 15px; height: 15px; flex: none; border-radius: 50%; border: 2px solid color-mix(in oklab, var(--at-acc) 30%, transparent); border-top-color: var(--at-acc); animation: at-spin 0.8s linear infinite; }
@keyframes at-spin { to { transform: rotate(360deg); } }
.result-pill { display: none; align-items: center; gap: 9px; font-size: 13px; font-weight: 700; color: #8fe9f5; }
.result-pill.show { display: flex; }
.result-pill svg { flex: none; color: var(--at-acc); }
.err-note { display: flex; align-items: center; gap: 9px; font-size: 12.5px; color: #ff8585; line-height: 1.5; }
.btn[disabled] { cursor: default; }

.ws-actions { display: flex; flex-direction: column; gap: 10px; margin-top: auto; }
.ws-actions .btn-amber { background: linear-gradient(135deg, #0891b2, var(--at-acc)); color: #04232b; }
.btn-ghost { background: none; border: none; color: var(--tx-dim); cursor: pointer; }
.btn-ghost:hover { color: var(--tx); }

/* merge */
.ws-merge { padding: 26px; margin: 0 auto; }
.m-head { display: flex; align-items: center; gap: 13px; margin-bottom: 14px; }
.m-head .meta { min-width: 0; flex: 1; }
.alist { display: flex; flex-direction: column; gap: 9px; }
.arow { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid var(--line); border-radius: 12px; background: var(--surface-2); }
.arow.dragging { opacity: 0.5; border-color: var(--at-acc); }
.arow .drag { color: var(--tx-faint); cursor: grab; flex: none; display: grid; place-items: center; }
.arow .drag:active { cursor: grabbing; }
.arow .drag svg { width: 16px; height: 16px; }
.arow .ord { font-family: var(--font-h); font-weight: 700; color: #8fe9f5; width: 20px; flex: none; }
.arow .aico { width: 34px; height: 34px; border-radius: 9px; flex: none; display: grid; place-items: center; background: color-mix(in oklab, var(--at-acc) 16%, transparent); color: var(--at-acc); }
.arow .aico svg { width: 18px; height: 18px; }
.arow .an { flex: 1; min-width: 0; font-size: 13.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.arow .adel { color: var(--tx-faint); cursor: pointer; flex: none; padding: 5px; border-radius: 7px; }
.arow .adel:hover { color: #ff6f9c; background: rgba(255, 255, 255, 0.05); }
.arow .adel svg { width: 16px; height: 16px; }
.ws-merge .note { margin: 4px 0; }
.ws-merge .ws-actions { margin-top: 4px; }

/* recorder */
.rec-stage { border: 1px solid var(--line); border-radius: 22px; background: var(--surface); padding: 40px 30px; text-align: center; min-height: 486px; display: flex; flex-direction: column; justify-content: center; }
.rec-timer { font-family: var(--font-h); font-weight: 700; font-size: 54px; color: var(--tx); font-variant-numeric: tabular-nums; letter-spacing: 0.02em; }
.rec-status { color: var(--tx-faint); font-weight: 600; font-size: 14px; margin-top: 6px; }
.rec-viz { display: flex; align-items: flex-end; justify-content: center; gap: 4px; height: 80px; margin: 26px 0; }
.rec-viz span { width: 5px; background: var(--at-acc); border-radius: 3px; height: 8px; transition: height 0.08s; }
.rec-btn { width: 80px; height: 80px; border-radius: 50%; border: none; background: linear-gradient(135deg, #0891b2, var(--at-acc)); color: #042; display: grid; place-items: center; cursor: pointer; margin: 0 auto; box-shadow: 0 10px 30px -8px rgba(34, 211, 238, 0.6); transition: 0.18s; }
.rec-btn:hover { transform: scale(1.05); }
.rec-btn.recording { background: linear-gradient(135deg, #dc2626, #f87171); animation: at-pulse 1.4s infinite; }
@keyframes at-pulse { 0%, 100% { box-shadow: 0 10px 30px -8px rgba(220, 38, 38, 0.6); } 50% { box-shadow: 0 10px 40px -4px rgba(220, 38, 38, 0.9); } }
.rec-btn svg { width: 34px; height: 34px; }
.rec-actions { display: none; gap: 10px; justify-content: center; margin-top: 26px; flex-wrap: wrap; }
.rec-actions.show { display: flex; }
.rec-actions .btn-amber { background: linear-gradient(135deg, #0891b2, var(--at-acc)); color: #04232b; }
.rec-player { display: none; margin-top: 24px; }
.rec-player.show { display: block; }
.rec-player audio { width: 100%; }
.rec-hint { color: var(--tx-faint); font-size: 13px; margin-top: 14px; }

/* toast */
.at-toast { position: fixed; bottom: 26px; left: 50%; transform: translateX(-50%) translateY(20px); background: var(--surface); border: 1px solid var(--at-acc); color: var(--tx); padding: 12px 18px; border-radius: 11px; font-weight: 600; font-size: 13.5px; box-shadow: 0 18px 50px -12px rgba(0, 0, 0, 0.8); opacity: 0; pointer-events: none; transition: 0.25s; z-index: 120; max-width: 90vw; }
.at-toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }

@media (max-width: 760px) {
  .ws-grid { grid-template-columns: 1fr; }
  .ws-panel { border-left: none; border-top: 1px solid var(--line); }
  .dz { padding: 46px 20px; }
}
</style>
